// Globals
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { getRobotsData, seoSelector, updateRobotsData } from '../../feature';

// Components
import { StyledPageTitle, StyledPageWrapper } from '../../components/Styled';
import { StyledCreateButton, StyledHeaderBlock, StyledTabsWrap, StyledContentBlock } from './SeoRobotsContainerStyles';
import { CheckmarkIcon, EditIcon } from '@/icons';
import { showSuccessToast, useForm } from '@/components';
import { RobotsInfo, UpdateRobotsForm } from '../../components';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Helpers
import { useBoolean } from '@/hooks';

const SeoRobotsContainer = () => {
  const { t } = useTranslation(['seo']);
  const { canManage } = usePermissions();
  const canManageSeo = canManage(Resource.SEO);
  const dispatch = useAppDispatch();
  const { robotsData, isLoading } = useAppState(seoSelector);
  const { value: isEditMode, toggle: toggleEditing } = useBoolean();
  const [form] = useForm();

  const handleSubmit = useCallback(async () => {
    try {
      const values = form.getFieldsValue();
      await dispatch(updateRobotsData(values));
      showSuccessToast({ message: t('seo_data_saved') });
    } catch (error) {}
  }, []);

  const onToggleEdit = async () => {
    if (isEditMode) {
      await handleSubmit();
    }
    toggleEditing();
  };

  useEffect(() => {
    if (!robotsData) {
      dispatch(getRobotsData());
    }
  }, [robotsData]);

  return (
    <StyledPageWrapper>
      <StyledHeaderBlock>
        <StyledPageTitle>{t('seo_robots_tab_title')}</StyledPageTitle>
        <StyledTabsWrap>
          {canManageSeo && (
            <StyledCreateButton htmlType={'submit'} onClick={onToggleEdit}>
              {isEditMode ? <CheckmarkIcon /> : <EditIcon />}
              {t(isEditMode ? 'seo_page_save_button' : 'seo_page_edit_button')}
            </StyledCreateButton>
          )}
        </StyledTabsWrap>
      </StyledHeaderBlock>
      <StyledContentBlock>
        {canManageSeo && isEditMode ? (
          <UpdateRobotsForm
            handleSubmit={handleSubmit}
            isLoading={isLoading}
            formInstance={form}
            initialValues={robotsData}
          />
        ) : (
          <RobotsInfo robotsData={robotsData} />
        )}
      </StyledContentBlock>
    </StyledPageWrapper>
  );
};

export default SeoRobotsContainer;
